import { DEFAULT_RECONNECT_INTERVAL_MS, DEFAULT_RECONNECT_LIMIT, isEventSourceSupported, ReadyState, } from './constants';
import { setUpSocketIOPing } from './socket-io';
import { assertIsWebSocket } from './util';
const bindMessageHandler = (webSocketInstance, optionsRef, setLastMessage) => {
    webSocketInstance.onmessage = (message) => {
        optionsRef.current.onMessage && optionsRef.current.onMessage(message);
        if (typeof optionsRef.current.filter === 'function' &&
            optionsRef.current.filter(message) !== true) {
            return;
        }
        setLastMessage(message);
    };
};
const bindOpenHandler = (webSocketInstance, optionsRef, setReadyState, reconnectCount) => {
    webSocketInstance.onopen = (event) => {
        optionsRef.current.onOpen && optionsRef.current.onOpen(event);
        reconnectCount.current = 0;
        setReadyState(ReadyState.OPEN);
    };
};
const bindCloseHandler = (webSocketInstance, optionsRef, setReadyState, reconnect, reconnectCount) => {
    if (isEventSourceSupported && webSocketInstance instanceof EventSource) {
        return () => { };
    }
    assertIsWebSocket(webSocketInstance);
    let reconnectTimeout;
    webSocketInstance.onclose = (event) => {
        var _a, _b;
        optionsRef.current.onClose && optionsRef.current.onClose(event);
        setReadyState(ReadyState.CLOSED);
        if (optionsRef.current.shouldReconnect &&
            optionsRef.current.shouldReconnect(event)) {
            const reconnectAttempts = (_a = optionsRef.current.reconnectAttempts) !== null && _a !== void 0 ? _a : DEFAULT_RECONNECT_LIMIT;
            if (reconnectCount.current < reconnectAttempts) {
                reconnectTimeout = window.setTimeout(() => {
                    reconnectCount.current++;
                    reconnect();
                }, (_b = optionsRef.current.reconnectInterval) !== null && _b !== void 0 ? _b : DEFAULT_RECONNECT_INTERVAL_MS);
            }
            else {
                optionsRef.current.onReconnectStop &&
                    optionsRef.current.onReconnectStop(reconnectAttempts);
                // eslint-disable-next-line no-console
                console.warn(`Max reconnect attempts of ${reconnectAttempts} exceeded`);
            }
        }
    };
    return () => reconnectTimeout && window.clearTimeout(reconnectTimeout);
};
const bindErrorHandler = (webSocketInstance, optionsRef, setReadyState, reconnect, reconnectCount) => {
    let reconnectTimeout;
    webSocketInstance.onerror = (error) => {
        var _a, _b;
        optionsRef.current.onError && optionsRef.current.onError(error);
        if (isEventSourceSupported && webSocketInstance instanceof EventSource) {
            optionsRef.current.onClose &&
                optionsRef.current.onClose(Object.assign(Object.assign({}, error), { code: 1006, reason: `An error occurred with the EventSource: ${error}`, wasClean: false }));
            setReadyState(ReadyState.CLOSED);
            webSocketInstance.close();
        }
        if (optionsRef.current.retryOnError) {
            if (reconnectCount.current <
                ((_a = optionsRef.current.reconnectAttempts) !== null && _a !== void 0 ? _a : DEFAULT_RECONNECT_LIMIT)) {
                reconnectTimeout = window.setTimeout(() => {
                    reconnectCount.current++;
                    reconnect();
                }, (_b = optionsRef.current.reconnectInterval) !== null && _b !== void 0 ? _b : DEFAULT_RECONNECT_INTERVAL_MS);
            }
            else {
                optionsRef.current.onReconnectStop &&
                    optionsRef.current.onReconnectStop(optionsRef.current.reconnectAttempts);
                // eslint-disable-next-line no-console
                console.warn(`Max reconnect attempts of ${optionsRef.current.reconnectAttempts} exceeded`);
            }
        }
    };
    return () => reconnectTimeout && window.clearTimeout(reconnectTimeout);
};
export const attachListeners = (webSocketInstance, setters, optionsRef, reconnect, reconnectCount, sendMessage) => {
    const { setLastMessage, setReadyState } = setters;
    let interval;
    if (optionsRef.current.fromSocketIO) {
        interval = setUpSocketIOPing(sendMessage);
    }
    bindMessageHandler(webSocketInstance, optionsRef, setLastMessage);
    bindOpenHandler(webSocketInstance, optionsRef, setReadyState, reconnectCount);
    const cancelReconnectOnClose = bindCloseHandler(webSocketInstance, optionsRef, setReadyState, reconnect, reconnectCount);
    const cancelReconnectOnError = bindErrorHandler(webSocketInstance, optionsRef, setReadyState, reconnect, reconnectCount);
    return () => {
        setReadyState(ReadyState.CLOSING);
        cancelReconnectOnClose();
        cancelReconnectOnError();
        webSocketInstance.close();
        if (interval) {
            clearInterval(interval);
        }
    };
};
